import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const EditColumnsPopover = ({ isOpen, onClose, onApply, position, visibleColumns, onColumnToggle, onResetColumns }) => {
  const { t } = useTranslation();
  const [localVisibleColumns, setLocalVisibleColumns] = useState(visibleColumns);
  const popoverRef = useRef(null);

  const allColumns = [
    { id: 'checkbox', name: t('transactions.editColumns.checkbox'), required: true },
    { id: 'amount', name: t('transactions.editColumns.amount'), required: false },
    { id: 'currency', name: t('transactions.editColumns.currency'), required: false },
    { id: 'status', name: t('transactions.editColumns.status'), required: false },
    // { id: 'type', name: 'Type', required: false },
    { id: 'receivingWallet', name: t('transactions.editColumns.receivingWallet'), required: false },
    { id: 'transactionId', name: t('transactions.editColumns.transactionId'), required: false },
    { id: 'invoiceNo', name: t('transactions.editColumns.invoiceId'), required: false },
    { id: 'date', name: t('transactions.editColumns.date'), required: false },
  ];

  // Update local state when visibleColumns prop changes
  useEffect(() => {
    setLocalVisibleColumns(visibleColumns);
  }, [visibleColumns]);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleColumnToggle = (columnId) => {
    const column = allColumns.find(col => col.id === columnId);
    if (column && column.required) return; // Don't allow toggling required columns
    
    setLocalVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  const handleApply = () => {
    onApply(localVisibleColumns);
    onClose();
  };

  const handleCancel = () => {
    setLocalVisibleColumns(visibleColumns); // Reset to original state
    onClose();
  };

  const handleResetToDefaults = () => {
    const defaultColumns = {
      checkbox: true,
      amount: true,
      currency: true,
      status: true,
      type: true,
      receivingWallet: true,
      transactionId: true,
      invoiceNo: true,
      date: true,
    };
    setLocalVisibleColumns(defaultColumns);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-64 max-w-[calc(100vw-2rem)]"
      style={{
        top: position?.top || 0,
        left: position?.left || 0,
        transform: 'translateX(0)',
      }}
    >
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-900">{t('transactions.editColumns.title')}</h3>
        <p className="text-xs text-gray-500 mt-1">{t('transactions.editColumns.description')}</p>
      </div>

      {/* Column Options */}
      <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
        {allColumns.map((column) => (
          <label
            key={column.id}
            className={`flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-gray-50 ${
              column.required ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <input
              type="checkbox"
              checked={localVisibleColumns[column.id] || false}
              onChange={() => handleColumnToggle(column.id)}
              disabled={column.required}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 flex-1">{column.name}</span>
            {column.required && (
              <span className="text-xs text-gray-400">{t('transactions.editColumns.required')}</span>
            )}
          </label>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 justify-between">
        {/* <button
          onClick={handleResetToDefaults}
          className="px-3 py-2 text-xs font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-lg transition-colors"
        >
          Reset to Defaults
        </button> */}
        <div className="flex gap-2">
          <button
            onClick={handleCancel}
            className="px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {t('transactions.editColumns.cancel')}
          </button>
          <button
            onClick={handleApply}
             style={{
                background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                borderRadius: "10px",
              }}
            className="px-3 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            {t('transactions.editColumns.apply')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditColumnsPopover;
